import { ReactNode } from 'react';
import { MarginBottomProps, MinHeightProps, PaddingProps } from 'styled-system';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { gutterWidth } from 'theme';

import {
  HeroBackgroundContainer,
  HeroBackgroundImageContainer,
  HeroBackgroundMobileImageContainer,
} from './Hero.styled';

type StyleProps = Pick<PaddingProps, 'pt' | 'pb' | 'px' | 'py'> &
  MinHeightProps &
  Pick<MarginBottomProps, 'mb'>;

type Props = {
  heroImage: ApiFilestackImageData;
  mobileHeroImage?: ApiFilestackImageData;
  children: ReactNode;
  withOverlay?: boolean;
  withLightOverlay?: boolean;
  imageHeight?: ApiFilestackImageData['height'];
  'data-qa-id'?: string;
} & StyleProps;

export function Hero({
  heroImage,
  mobileHeroImage,
  children,
  withOverlay,
  withLightOverlay,
  imageHeight = 1024,
  'data-qa-id': dataQaId,
  px,
  py,
  pt,
  pb,
  minHeight,
  mb,
}: Props) {
  return (
    <Box
      position="relative"
      display="flex"
      width={1}
      minHeight={minHeight || ['320px', null, null, '450px']}
      pt={pt}
      pb={pb}
      mb={mb}
      px={px || [gutterWidth / 2, null, null, gutterWidth]}
      py={py || [48, null, null, 96]}
      justifyContent="center"
      alignItems="center"
      color="white"
      data-qa-id={dataQaId}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        width={1}
        height="100%"
        overflow="hidden"
      >
        {(heroImage || mobileHeroImage) && (
          <HeroBackgroundContainer>
            {heroImage && (
              <HeroBackgroundImageContainer
                $hasMobileImage={Boolean(mobileHeroImage)}
              >
                <FilestackImage
                  data-qa-id="hero-image-image"
                  width={1920}
                  height={imageHeight}
                  expandSize
                  objectFit="cover"
                  alt={getText('Hero image')}
                  data={heroImage}
                  eagerLoad
                />
              </HeroBackgroundImageContainer>
            )}

            {mobileHeroImage && (
              <HeroBackgroundMobileImageContainer>
                <FilestackImage
                  data={mobileHeroImage}
                  width={992}
                  height={605}
                  expandSize
                  objectFit="cover"
                  alt={getText('Hero image')}
                  eagerLoad
                />
              </HeroBackgroundMobileImageContainer>
            )}
          </HeroBackgroundContainer>
        )}

        {(withOverlay || withLightOverlay) && (
          <Box
            position="absolute"
            transform="translateZ(0)"
            top={0}
            left={0}
            width={1}
            height="100%"
            bg={`rgba(0,0,0,${withLightOverlay ? 0.2 : 0.4})`}
          />
        )}
      </Box>

      <Box
        position="relative"
        transform="rotateZ(0)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
      >
        {children}
      </Box>
    </Box>
  );
}
